import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  UserPlus, Users, ClipboardList, CalendarDays, TrendingUp, TrendingDown,
  Plus, ArrowRight, AlertTriangle, CheckCircle2, CalendarClock, ShieldCheck, Clock,
} from "lucide-react";
import { PageHead } from "@/components/AppLayout";
import { Card, CardHead, Btn, Badge, Avatar, EmptyState, statusTone } from "@/components/ui-kit";
import { useAuth } from "@/context/AuthContext";
import { useDashboardLayout } from "@/hooks/useDashboardLayout";
import { DashboardCustomizer } from "@/components/DashboardCustomizer";
import ProDocuments from "@/pages/ProDocuments";
import api from "@/lib/api";

const DOCTOR_WIDGETS = [
  { id: "kpis", label: "Key metrics" },
  { id: "recent_orders", label: "Recent treatment orders" },
  { id: "at_risk", label: "At-risk patients" },
  { id: "tasks", label: "Today's tasks" },
  { id: "reviews", label: "Upcoming reviews" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [patients, setPatients] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [docs, setDocs] = useState(null);
  const [showDocs, setShowDocs] = useState(false);

  const load = useCallback(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    api.get("/referrals").then((r) => setReferrals(r.data));
    api.get("/patients").then((r) => setPatients(r.data));
    api.get("/tasks").then((r) => setTasks(r.data));
  }, []);
  const loadDocs = useCallback(() => {
    if (!user?.id) return Promise.resolve();
    return api.get(`/users/${user.id}/documents`).then((r) => setDocs(r.data));
  }, [user?.id]);
  useEffect(() => { load(); loadDocs(); }, [load, loadDocs]);

  const verified = user?.credential_status === "Verified";

  const toggleTask = async (t) => {
    await api.put(`/tasks/${t.id}`, { done: !t.done });
    setTasks(tasks.map((x) => (x.id === t.id ? { ...x, done: !x.done } : x)));
  };

  const atRisk = patients.filter((p) => p.status === "At Risk" || p.status === "Review Needed");
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const fname = (user?.name || "Doctor").replace(/^Dr\.?\s*/i, "").split(" ")[0];

  const STAT = [
    { k: "new_referrals", label: "New Orders", icon: UserPlus, tone: "bg-sky-50 text-sky-600" },
    { k: "active_patients", label: "Active Patients", icon: Users, tone: "bg-teal-50 text-teal-600" },
    { k: "review_requests", label: "Review Requests", icon: ClipboardList, tone: "bg-violet-50 text-violet-600" },
    { k: "todays_followups", label: "Open Tasks", icon: CalendarDays, tone: "bg-amber-50 text-amber-600" },
  ];

  const layout = useDashboardLayout(`doctor-dashboard:${user?.id || "anon"}`, DOCTOR_WIDGETS);

  if (showDocs) return (
    <div className="max-w-2xl mx-auto">
      <ProDocuments userId={user.id} role="doctor" credentialStatus={user?.credential_status}
        subtitle="Upload your credentials so PranIQ Admin can verify your practice profile."
        onDone={async () => { await loadDocs(); setShowDocs(false); }} />
    </div>
  );

  const upcomingReviews = patients.filter((p) => p.next_review);
  const W = {
    kpis: (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 stagger">
        {STAT.map((s) => {
          const delta = stats?.deltas?.[s.k];
          return (
          <Card key={s.k} hover className="p-5 flex gap-3.5 items-start" data-testid={`kpi-${s.k.replace(/_/g, "-")}`}>
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${s.tone}`}><s.icon size={20} /></div>
            <div>
              <div className="metric text-2xl font-extrabold text-slate-900 leading-none">{stats ? stats[s.k] : "–"}</div>
              <div className="text-[12.5px] text-slate-500 mt-1">{s.label}</div>
              {delta > 0 && <div className="text-[11px] font-semibold text-emerald-600 mt-0.5 flex items-center gap-0.5"><TrendingUp size={11} /> {delta} today</div>}
            </div>
          </Card>
          );
        })}
      </div>
    ),
    recent_orders: (
      <Card>
        <CardHead title="Recent Treatment Orders" action={<button className="text-[12.5px] font-bold text-teal-700" onClick={() => nav("/referrals")}>View all</button>} />
        {referrals.length === 0 ? (
          <EmptyState icon={<ClipboardList size={24} />} title="No treatment orders yet"
            subtitle="Create your first treatment order to start building a patient's care pathway."
            action={<Btn variant="primary" onClick={() => nav("/referrals/new")}><Plus size={16} /> New Treatment Order</Btn>} />
        ) : (
          <div className="overflow-x-auto"><table className="w-full">
            <thead><tr className="text-left">
              {["Patient", "Diagnosis", "Referred", "Status", "Next Action"].map((h) => (
                <th key={h} className="pq-label px-5 py-2.5 border-b border-slate-100">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {referrals.slice(0, 5).map((r) => (
                <tr key={r.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer" onClick={() => nav(`/referrals/${r.id}`)} data-testid={`referral-row-${r.id}`}>
                  <td className="px-5 py-3"><div className="flex items-center gap-2.5"><Avatar name={r.patient_name} size={34} /><div><div className="font-semibold text-[13.5px]">{r.patient_name}</div><div className="text-[11.5px] text-slate-500">{r.patient_meta}</div></div></div></td>
                  <td className="px-5 py-3 text-[13px]">{r.diagnosis_label}</td>
                  <td className="px-5 py-3 text-[13px] text-slate-500">{new Date(r.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</td>
                  <td className="px-5 py-3"><Badge tone={statusTone(r.status)}>{r.status}</Badge></td>
                  <td className="px-5 py-3"><div className="text-[13px] font-semibold">{r.next_action}</div><div className="text-[11.5px] text-slate-500">📅 {r.next_date}</div></td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </Card>
    ),
    at_risk: (
      <Card>
        <CardHead title={<span className="flex items-center gap-2"><AlertTriangle size={17} className="text-amber-500" /> At-Risk Patients</span>}
          action={<button className="text-[12.5px] font-bold text-teal-700" onClick={() => nav("/patients")}>View all</button>} />
        {atRisk.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-slate-500">No at-risk patients. Everyone is on track 🎉</div>
        ) : atRisk.slice(0, 4).map((p) => (
          <div key={p.id} className="flex items-center gap-3 px-5 py-3.5 border-b border-slate-100 last:border-0" data-testid={`at-risk-${p.id}`}>
            <Avatar name={p.name} size={36} />
            <div className="flex-1"><div className="font-semibold text-[13.5px]">{p.name}</div><div className="text-[11.5px] text-slate-500">{p.age} · {p.gender} · {p.diagnosis}</div></div>
            <Badge tone={statusTone(p.status)}>{p.status}</Badge>
            <Btn size="sm" onClick={() => nav("/patients")}>Take Action</Btn>
          </div>
        ))}
      </Card>
    ),
    tasks: (
      <Card>
        <CardHead title={<span className="flex items-center gap-2"><CheckCircle2 size={17} className="text-teal-600" /> Today's Tasks</span>} />
        {tasks.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-slate-500">No tasks yet. Add follow-ups from patient records.</div>
        ) : tasks.map((t) => (
          <div key={t.id} className="flex gap-3 px-5 py-3 border-b border-slate-100 last:border-0 items-start" data-testid={`task-${t.id}`}>
            <input type="checkbox" checked={!!t.done} onChange={() => toggleTask(t)} className="mt-1 accent-teal-600" data-testid={`task-checkbox-${t.id}`} />
            <div className="flex-1"><div className={`text-[13px] font-semibold ${t.done ? "line-through text-slate-400" : ""}`}>{t.title}</div><div className="text-[11.5px] text-slate-500">{t.meta}</div></div>
            <div className="text-[11.5px] text-slate-500 whitespace-nowrap">{t.time}</div>
          </div>
        ))}
      </Card>
    ),
    reviews: (
      <Card>
        <CardHead title={<span className="flex items-center gap-2"><CalendarClock size={17} className="text-teal-600" /> Upcoming Reviews</span>} />
        {upcomingReviews.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-slate-500">No reviews scheduled.</div>
        ) : upcomingReviews.slice(0, 4).map((p) => (
          <div key={p.id} className="flex gap-3 px-5 py-3 border-b border-slate-100 last:border-0 items-center">
            <Avatar name={p.name} size={34} />
            <div className="flex-1"><div className="font-semibold text-[13px]">{p.name}</div><div className="text-[11.5px] text-slate-500">{p.diagnosis}</div></div>
            <div className="text-right"><div className="text-[11.5px] text-slate-500">{p.next_review}</div><Badge tone="purple">Review</Badge></div>
          </div>
        ))}
      </Card>
    ),
  };

  return (
    <>
      <PageHead
        title={<>{greet}, Dr. {fname} <span className="inline-block">👋</span></>}
        subtitle="Here's what's happening with your practice today."
        action={<DashboardCustomizer {...layout} />}
      />

      {docs && !verified && (
        <button onClick={() => setShowDocs(true)} data-testid="doctor-credentials-banner"
          className="w-full flex items-center gap-2 rounded-xl px-3 py-2.5 text-[12.5px] font-medium mb-5 bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors">
          <Clock size={15} />
          <span className="text-left flex-1">{docs.length === 0 ? "Documents pending — please upload your credentials within 7 days for PranIQ verification" : "Documents pending PranIQ verification"}</span>
          <span className="font-semibold underline">Manage →</span>
        </button>
      )}
      {verified && (
        <div className="w-full flex items-center gap-2 rounded-xl px-3 py-2.5 text-[12.5px] font-medium mb-5 bg-emerald-50 text-emerald-700" data-testid="doctor-verified-banner">
          <ShieldCheck size={15} />
          <span className="flex-1">PranIQ verified — your credentials are approved.</span>
        </div>
      )}

      <div className="space-y-5">
        {layout.items.filter((i) => i.visible).map((i) => (
          <div key={i.id} data-testid={`dash-widget-${i.id}`}>{W[i.id]}</div>
        ))}
      </div>
    </>
  );
}
